import React from 'react';
import { Chip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface ContentProcessingBadgeProps {
  /** Content object with a `processingState` field. */
  content: { processingState?: string | null };
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * States where AI processing is still in flight (or recently completed and
 * worth surfacing). Matches the original ContentCard behavior.
 */
const AI_PROCESSING_STATES = new Set([
  'uploaded',
  'metadata_extracted',
  'text_extracted',
  'ai_enrichment_pending',
  'ai_enriched',
  'embedding_pending',
  'embedding_complete',
]);

/**
 * Renders an "AI Processing" chip when the content's processingState is in
 * the AI processing pipeline. Returns null otherwise — safe to drop into any
 * layout without conditional logic at the call site.
 */
export const ContentProcessingBadge: React.FC<ContentProcessingBadgeProps> = ({ content, sx }) => {
  if (!content.processingState || !AI_PROCESSING_STATES.has(content.processingState)) {
    return null;
  }
  return (
    <Chip
      size="small"
      label="AI Processing"
      color="info"
      variant="outlined"
      sx={[
        { fontSize: '0.65rem' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

export default ContentProcessingBadge;
